import { useState, useEffect } from 'react';

// Types
interface PWADetectionResult {
  isIOS: boolean;
  isPWA: boolean;
  canInstall: boolean;
  needsInstallation: boolean;
  notificationCompatible: boolean;
  iosVersion: number;
}

// PWA Manager Class
class PWAManager {
  private deferredPrompt: any = null;

  // Detect iOS version
  static checkIOSVersion(): number {
    const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
    if (match) {
      return parseFloat(`${match[1]}.${match[2]}`);
    }
    return 0;
  }

  // Check if running as PWA (standalone mode)
  static isPWAMode(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || 
           (window.navigator as any).standalone === true;
  }

  // Check if iOS PWA compatible for notifications
  static isIOSPWACompatible(): boolean {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isPWA = this.isPWAMode();
    const isIOSVersion16Plus = this.checkIOSVersion() >= 16.4;
    
    return isIOS && isPWA && isIOSVersion16Plus;
  }

  // Comprehensive PWA status check
  static async checkPWAStatus(): Promise<PWADetectionResult> {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isPWA = this.isPWAMode();
    const iosVersion = this.checkIOSVersion();
    
    return {
      isIOS,
      isPWA,
      canInstall: !isPWA && isIOS, // Can install if not already PWA
      needsInstallation: isIOS && !isPWA,
      notificationCompatible: isPWA || !isIOS, // PWA mode required for iOS
      iosVersion
    };
  }

  static async checkOneSignalStatus(): Promise<{ success: boolean; reason?: string; instructions?: any }> {
    const status = await this.checkPWAStatus();
    
    // iOS requires PWA mode for notifications
    if (status.isIOS && !status.isPWA) {
      return {
        success: false,
        reason: 'needs_pwa_mode',
        instructions: {
          message: 'Please install the app to your home screen first.',
          steps: [
            'Tap the Share button',
            'Select "Add to Home Screen"',
            'Open the app from your home screen'
          ]
        }
      };
    }

    // iOS version check
    if (status.isIOS && status.iosVersion < 16.4) {
      return {
        success: false,
        reason: 'ios_version_too_old',
        instructions: {
          message: `iOS ${status.iosVersion} detected. iOS 16.4+ required for notifications. Please update your device.`
        }
      };
    }

    // Check if OneSignal is loaded
    // @ts-ignore
    if (!window.OneSignal) {
      return {
        success: false,
        reason: 'initialization_failed',
        instructions: {
          message: 'OneSignal failed to load. Please refresh the page.'
        }
      };
    }

    return { success: true };
  }

  // Listen for PWA install prompt
  setupInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      this.deferredPrompt = e;
      
      // Show your custom install button
      this.showInstallButton();
    });

    // Listen for app installation
    window.addEventListener('appinstalled', () => {
      console.log('PWA was installed');
      this.hideInstallButton();
      this.deferredPrompt = null;
    });
  }

  // Show install button for Android/Desktop
  showInstallButton() {
    const installButton = document.getElementById('pwa-install-button');
    if (installButton) {
      installButton.style.display = 'block';
    }
  }

  hideInstallButton() {
    const installButton = document.getElementById('pwa-install-button');
    if (installButton) {
      installButton.style.display = 'none';
    }
  }

  // Trigger PWA install prompt
  async promptInstall() {
    if (!this.deferredPrompt) {
      return false;
    }

    // Show the prompt
    this.deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await this.deferredPrompt.userChoice;
    
    console.log(`User response to the install prompt: ${outcome}`);
    
    // Clear the saved prompt since it can't be used again
    this.deferredPrompt = null;
    
    return outcome === 'accepted';
  }
}

// ✅ REACT HOOK - PROPERLY EXPORTED
export function usePWAManager() {
  const [pwaStatus, setPwaStatus] = useState<PWADetectionResult | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [oneSignalStatus, setOneSignalStatus] = useState<{
    success: boolean;
    reason?: string;
    instructions?: any;
  } | null>(null);

  useEffect(() => {
    async function checkStatus() {
      const status = await PWAManager.checkPWAStatus();
      setPwaStatus(status);

      // Check OneSignal status
      const osStatus = await PWAManager.checkOneSignalStatus();
      setOneSignalStatus(osStatus);
    }

    checkStatus();

    // Setup install prompt detection
    const pwaManager = new PWAManager();
    pwaManager.setupInstallPrompt();

    // Listen for install prompt availability
    const handleBeforeInstallPrompt = () => {
      setShowInstallPrompt(true);
    };

    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      // Re-check PWA status after installation
      checkStatus();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  return {
    pwaStatus,
    showInstallPrompt,
    oneSignalStatus,
    promptInstall: () => new PWAManager().promptInstall()
  };
}

// ✅ EXPORT CLASS FOR STANDALONE USE
export { PWAManager };

// ✅ ADD GLOBAL TYPES
declare global {
  interface Window {
    OneSignal: any;
  }
}